import {
  AWAY_MESSAGE_DEFAULT_TEXT,
  AWAY_MESSAGE_TEXT_MAX_LENGTH,
} from '../models/messaging-profile';
import { action } from '@ember/object';
import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { tracked } from '@glimmer/tracking';
import CharacterCounter from 'ember-simplepractice/components/spds/core/character-counter';
import Component from '@glimmer/component';
import GhostButton from 'ember-simplepractice/components/spds/action/button/ghost';
import MessagingPage from './messaging-page.gjs';
import PrimaryButton from 'ember-simplepractice/components/spds/action/button/primary';
import styles from './away-message-customizer.module.scss';
import svgJar from 'ember-svg-jar/helpers/svg-jar';

export default class AwayMessageCustomizer extends Component {
  @service alertManager;
  @service intl;
  @service messaging;

  @tracked customAwayMessage;

  messageMaxLength = AWAY_MESSAGE_TEXT_MAX_LENGTH;
  learnMoreUrl =
    'https://support.simplepractice.com/hc/en-us/articles/115003629583-Getting-started-with-Secure-Messaging#settingana';

  constructor() {
    super(...arguments);

    this.customAwayMessage =
      this.messaging.currentProfile?.awayMessageText || AWAY_MESSAGE_DEFAULT_TEXT;
  }

  get isMessageEmpty() {
    return this.customAwayMessage?.trim().length === 0;
  }

  get isSaveButtonDisabled() {
    return this.isMessageEmpty || this.customAwayMessage.length > this.messageMaxLength;
  }

  get isCustomAwayMessageUnsaved() {
    return this.customAwayMessage !== this.messaging.currentProfile?.awayMessageText;
  }

  handleTextareaInput = ({ target: textarea }) => {
    this.customAwayMessage = textarea.value;
  };

  @action
  goBack() {
    this.messaging.toggleAwayStatusCustomizing();
  }

  @action
  async onSave(message) {
    try {
      await this.messaging.updateAwayMessage.perform(message);

      this.alertManager.broadcast('toast', {
        message: this.intl.t('emc.away_message_customizer.update_success'),
        type: 'success',
      });

      this.goBack();
    } catch (error) {
      this.alertManager.broadcast('toast', {
        message: this.intl.t('emc.away_message_customizer.update_failed'),
        type: 'error',
        cta: {
          text: this.intl.t('emc.away_message_customizer.retry_button'),
          action: () => this.onSave(message),
        },
      });
    }
  }

  <template>
    <MessagingPage class="{{styles.component}} away-message-customizer-page">
      <:header>
        <GhostButton
          @fitContent={{true}}
          @iconOnly={{true}}
          {{on "click" this.goBack}}
          class="back-button"
          data-test-away-message-customizer-page-back-button
        >
          {{svgJar "messaging-chevron-left"}}
        </GhostButton>

        <span class="title">{{t "emc.away_message_customizer.title"}}</span>
      </:header>
      <:default>
        <div class="content" data-test-away-message-customizer-page-content>
          <p>
            {{t "emc.away_message_customizer.description"}}
            <a href={{this.learnMoreUrl}} target="_blank" rel="noopener noreferrer">{{t
                "emc.away_message_customizer.learn_more"
              }}</a>
          </p>

          <label for="custom-away-message-textarea" class="input-label">
            {{t "emc.away_message_customizer.input_label"}}
          </label>

          <div class="input-area">
            <textarea
              id="custom-away-message-textarea"
              class="text-input"
              value={{this.customAwayMessage}}
              placeholder={{t "emc.away_message_customizer.input_placeholder"}}
              {{on "input" this.handleTextareaInput}}
              data-test-custom-away-message-input
            />
          </div>
          <div class="input-sub-text">
            {{#if this.isMessageEmpty}}
              <div class="error-message" data-test-message-required-error>
                {{t "emc.away_message_customizer.message_required"}}
              </div>
            {{/if}}

            <CharacterCounter @limit={{this.messageMaxLength}} @value={{this.customAwayMessage}} />
          </div>
        </div>
      </:default>
      <:footer>
        <PrimaryButton
          @fitContent={{true}}
          {{on "click" (fn this.onSave this.customAwayMessage)}}
          disabled={{this.isSaveButtonDisabled}}
          data-test-message-save
        >
          {{t "emc.away_message_customizer.save_button"}}
        </PrimaryButton>

        {{#if this.isCustomAwayMessageUnsaved}}
          <span class="unsaved-indicator" data-test-unsaved-indicator>
            {{t "emc.away_message_customizer.unsaved_changes"}}
          </span>
        {{/if}}
      </:footer>
    </MessagingPage>
  </template>
}
